import { useNavigate } from "react-router-dom";
import useMediaByValue from "hooks/useMediaByValue";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseButtonIcon from "assets/icons/Close";
import CustomButton from "components/Button";
import { IGlobalNotification } from "interfaces/Notifications";
import { useNotificationsStore } from "store/notificationsStore";

interface IGlobalNotificationModalProps {
  open: boolean;
  setIsModalOpen: (value: boolean) => void;
  notificationData: IGlobalNotification;
}

const GlobalNotificationModal = ({ open, setIsModalOpen, notificationData }: IGlobalNotificationModalProps) => {
  const isMediumViewPort = useMediaByValue("(min-width:600px)");
  const navigate = useNavigate();
  const { globalNotificationsArr, saveGlobalNotifications } = useNotificationsStore();
  const { _id, title, description, background, image, button } = notificationData;
  const { isVisible, label, link } = button;
  const updatedGlobalNotificationsArr = globalNotificationsArr.filter((notification) => notification._id !== _id);

  const handleModalClose = () => {
    saveGlobalNotifications(updatedGlobalNotificationsArr);
    setIsModalOpen(false);
  };

  const handleModalButtonClick = () => {
    if (!link) {
      handleModalClose();
      return;
    }

    try {
      const url = new URL(link);

      if (url.origin === window.location.origin) {
        handleModalClose();
        navigate(url.pathname + url.search + url.hash);
      } else {
        handleModalClose();
        window.open(url.toString(), "_blank", "noopener noreferrer");
      }
    } catch {
      handleModalClose();
      navigate(link);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-push-template-title"
      aria-describedby="modal-push-template-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          width: "90%",
          maxWidth: "800px",
          height: "auto",
          maxHeight: "90dvh",
          margin: "32px auto 0px auto",
          borderRadius: "8px",
          bgcolor: { background },
          boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
          overflowY: "auto",
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            "& path": { stroke: background === "#111" ? "#FFFFFF" : "#2E263D" },
          }}
          aria-label="modal-close-button"
          onClick={handleModalClose}
        >
          <CloseButtonIcon />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "64px 32px 48px 32px",
            flex: isMediumViewPort ? "0 0 60%" : "0 0 100%",
            minWidth: 0,
          }}
        >
          {title && (
            <Typography
              sx={{
                marginBottom: "16px",
                fontFamily: "var(--cygreMedium), sans-serif",
                fontWeight: 500,
                fontSize: "32px",
                lineHeight: 1.32,
                color: background === "#111" ? "#FFFFFF" : "#2E263D",
              }}
            >
              {title}
            </Typography>
          )}
          <Typography
            sx={{
              marginBottom: "24px",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: 1.42,
              color: background === "#111" ? "#FFFFFF" : "#2E263D",
            }}
          >
            {description}
          </Typography>
          {isVisible && (
            <CustomButton
              width={"max-content"}
              height={50}
              textButton={label}
              customBgColor="#F4701B"
              customHoverColor="#f0670b"
              customBorderRadius="8px"
              customFontSize="16px"
              customPadding="16px 24px"
              customTextTransform="none"
              onClick={handleModalButtonClick}
            />
          )}
        </Box>
        {isMediumViewPort && (
          <Box
            sx={{
              flex: "0 0 40%",
              margin: "10px 0px 10px 0px",
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${image})`,
            }}
          />
        )}
      </Box>
    </Modal>
  );
};

export default GlobalNotificationModal;
